// React and core
import React, { memo, useCallback, useEffect, useMemo, useState, useContext } from "react";

// Components
import { LottieAnimation } from "@swiggy-private/core-ui";
import { HeaderStampContainer } from "./styled";

// Contexts
import { CartHeaderLottieContext } from "@cart-client/contexts/CartHeaderLottieContext";
import { CartOverlaysContext } from "@swiggy-private/checkout-core";

// Types and interfaces
import { HeaderStampLottieProps, LottieIds } from "@swiggy-private/cart-types";

// Utils and helpers
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { getFrameData, LottieType, StampFrameData } from "./helper";
import { lottieProps } from "./constants";

// Constants
import { CART_HEADER_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

const HeaderStampLottie = ({
    lottieArray,
    cartType,
    showFinalState,
    showStampLottie,
    setHeaderStampLottieShown,
}: HeaderStampLottieProps) => {
    const [hideLottie, setHideLottie] = useState(false);
    const [lottieFrameData, setLottieFrameData] = useState<StampFrameData>();
    const [lottieUrl, setLottieUrl] = useState<string>("");
    const { cartHeaderLottieState } = useContext(CartHeaderLottieContext);

    const lottieId = useMemo(() => {
        const stampLottie = lottieArray.find(lottie => lottie.type === LottieType.STAMP);
        return stampLottie?.lottie_id || "";
    }, [lottieArray]);

    const hideStampLottie = hideLottie || !showStampLottie || !cartHeaderLottieState.hasLottie || isEmpty(lottieUrl);

    const { onCloseOverlay, enabledOverlayId } = useContext(CartOverlaysContext);

    const handleError = useCallback(() => {
        if (enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
            onCloseOverlay();
            setHideLottie(true);
        }
        setHideLottie(true);
        setHeaderStampLottieShown(true);

        // TO DO: ADD NR LOGS AFTER MIGRATION
    }, [enabledOverlayId, onCloseOverlay, setHeaderStampLottieShown]);

    const onPlayComplete = useCallback(() => {
        if (!showFinalState && enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
            onCloseOverlay();
        }
        setHeaderStampLottieShown(true);
    }, [showFinalState, enabledOverlayId, onCloseOverlay, setHeaderStampLottieShown]);

    useEffect(() => {
        setLottieUrl(getCloudinaryUrl(lottieId) || "");
        setLottieFrameData(getFrameData(cartType, LottieType.STAMP));
    }, [cartType, lottieId]);

    if (hideStampLottie) {
        return null;
    }

    return (
        <HeaderStampContainer data-testid={CART_HEADER_DATA_TEST_IDS.HEADER_STAMP_LOTTIE}>
            <LottieAnimation
                {...lottieProps}
                fileName={lottieUrl}
                visible={showStampLottie ?? false}
                playLottie={showStampLottie ?? false}
                autoplay={showStampLottie ?? false}
                onError={handleError}
                isOnFileChangeEnabled={true}
                frame={
                    showFinalState && lottieFrameData?.final
                        ? { initial: lottieFrameData.final - 2, final: lottieFrameData.final }
                        : lottieFrameData
                }
                onPlayComplete={onPlayComplete}
            />
        </HeaderStampContainer>
    );
};

export default memo(HeaderStampLottie);
